import React from "react";
import { Collapse } from "antd";
import styled from "styled-components";

const { Panel } = Collapse;

// ─── Types ───────────────────────────────────────────────────────────────────

interface SubjectEntry {
  subjectId: number | null;
  subjectName: string;
  subjectCount: number;
}

interface SubjectSummary {
  PENDING?: SubjectEntry[];
  EVALUATED?: SubjectEntry[];
  CHALLENGED?: SubjectEntry[];
}

interface SubjectSummaryCardsProps {
  subjectSummary: SubjectSummary;
  totalSubmissions?: number;
  totalStudents?: number;
  summary: any;
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Wrapper = styled.div`
  margin-top: 16px;

  /* Override antd Collapse styles */
  .ant-collapse {
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  .ant-collapse > .ant-collapse-item {
    border-bottom: none;
  }

  .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    padding: 14px 20px;
    align-items: center;
  }

  .ant-collapse-content {
    border-top: 1px solid #f0f0f0;
  }

  .ant-collapse-content > .ant-collapse-content-box {
    padding: 16px 20px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #444;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlueLine = styled.div`
  width: 4px;
  height: 24px;
  background: #1890ff;
  border-radius: 4px;
`;

const TotalCount = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #111;

  span {
    font-size: 14px;
    font-weight: 400;
    color: #999;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const STATUS_CONFIG: Record<
  string,
  { borderColor: string; labelColor: string }
> = {
  EVALUATED: { borderColor: "#52c41a", labelColor: "#52c41a" },
  PENDING: { borderColor: "#faad14", labelColor: "#faad14" },
  CHALLENGED: { borderColor: "#ff4d4f", labelColor: "#ff4d4f" },
};

const SUBJECT_DOT_COLORS = [
  "#ff4d4f",
  "#faad14",
  "#52c41a",
  "#1890ff",
  "#722ed1",
];

const StatusCard = styled.div<{ borderColor: string }>`
  background: #ffffff;
  border: 1.5px solid ${(p) => p.borderColor};
  border-radius: 10px;
  padding: 18px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const StatusLabel = styled.div<{ color: string }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${(p) => p.color};
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;

  &::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.color};
  }
`;

const OverallRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f5f5f5;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 10px;
`;

const OverallLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #222;
`;

const OverallValue = styled.span<{ color: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => p.color};
`;

const SubjectRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 4px;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const SubjectName = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #555;
`;

const Dot = styled.span<{ color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

const SubjectValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #222;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const statusMapping = {
  EVALUATED: "evaluated",
  PENDING: "pending",
  CHALLENGED: "challenged",
};

const SubjectSummaryCards: React.FC<SubjectSummaryCardsProps> = ({
  subjectSummary,
  totalSubmissions = 0,
  totalStudents = 0,
  summary,
}) => {
  const statuses = (["EVALUATED", "PENDING", "CHALLENGED"] as const).filter(
    (s) => subjectSummary[s] && subjectSummary[s]!.length > 0
  );

  const panelHeader = (
    <HeaderContent>
      <HeaderLeft>
        <BlueLine />
        Total Submissions
      </HeaderLeft>
      <TotalCount>
        {totalSubmissions} <span>/ {totalStudents}</span>
      </TotalCount>
    </HeaderContent>
  );
  return (
    <Wrapper>
      <Collapse defaultActiveKey={["submissions"]} expandIconPosition="start">
        <Panel header={panelHeader} key="submissions">
          <CardsGrid>
            {statuses.map((status) => {
              const entries = subjectSummary[status] || [];
              const cfg = STATUS_CONFIG[status];

              return (
                <StatusCard key={status} borderColor={cfg.borderColor}>
                  <StatusLabel color={cfg.labelColor}>{status}</StatusLabel>

                  {/* Overall row */}
                  <OverallRow>
                    <OverallLabel>Overall</OverallLabel>
                    <OverallValue color={cfg.labelColor}>
                      {summary[statusMapping[status]]}
                      {totalSubmissions > 0 && (
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#aaa",
                            fontSize: 13,
                          }}
                        >
                          {" "}
                          / {totalSubmissions}
                        </span>
                      )}
                    </OverallValue>
                  </OverallRow>

                  {/* Per-subject rows */}
                  {entries.length > 1 &&
                    entries.map((entry, idx) => (
                      <SubjectRow key={entry.subjectId ?? entry.subjectName}>
                        <SubjectName>
                          <Dot
                            color={
                              SUBJECT_DOT_COLORS[
                                idx % SUBJECT_DOT_COLORS.length
                              ]
                            }
                          />
                          {entry.subjectName}
                        </SubjectName>
                        <SubjectValue>
                          {entry.subjectCount}
                          {totalSubmissions > 0 && (
                            <span
                              style={{
                                fontWeight: 400,
                                color: "#aaa",
                                fontSize: 12,
                              }}
                            >
                              {" "}
                              / {totalSubmissions}
                            </span>
                          )}
                        </SubjectValue>
                      </SubjectRow>
                    ))}
                </StatusCard>
              );
            })}
          </CardsGrid>
        </Panel>
      </Collapse>
    </Wrapper>
  );
};

export default SubjectSummaryCards;
